import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { Button } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeftIcon } from "lucide-react";
import type { UpdateRoomBookingDTO } from "../../domain/contracts/room-bookings";
import { useRoomBookingQuery, useUpdateRoomBooking } from "../api/queries";
import { RoomBookingForm } from "../ui/RoomBookingForm";

export function RoomBookingEditPage() {
  const { id } = useParams({ from: "/app/room-bookings/$id/edit" });
  const nav = useNavigate({ from: "/app/room-bookings/$id/edit" });
  const { data: booking, isLoading } = useRoomBookingQuery(id ?? "");
  const updateBooking = useUpdateRoomBooking(id ?? "");

  const roomBookingEditBreadcrumb = (
    <Breadcrumb
      items={[
        { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
        { label: "ການຈອງຫ້ອງ", to: "/app/room-bookings" },
        { label: "ແກ້ໄຂ" },
      ]}
    />
  );

  if (isLoading || !booking) {
    return (
      <>
        <Header>{roomBookingEditBreadcrumb}</Header>
        <Main>
          <div className="text-muted-foreground">ກຳລັງໂຫຼດ...</div>
        </Main>
      </>
    );
  }

  if (booking.status !== "scheduled") {
    return (
      <>
        <Header>{roomBookingEditBreadcrumb}</Header>
        <Main>
          <div className="text-muted-foreground">
            ບໍ່ສາມາດແກ້ໄຂການຈອງທີ່ສຳເລັດຫຼືຍົກເລີກແລ້ວ
          </div>
          <Button
            variant="outline"
            onClick={() => nav({ to: "/app/room-bookings" })}
            className="mt-4"
          >
            ກັບຄືນ
          </Button>
        </Main>
      </>
    );
  }

  const initialValues = {
    roomId: booking.roomId,
    date: booking.date,
    startTime:
      booking.startTime.length === 8
        ? booking.startTime.slice(0, 5)
        : booking.startTime,
    endTime:
      booking.endTime.length === 8
        ? booking.endTime.slice(0, 5)
        : booking.endTime,
    contactName: booking.contactName ?? "",
    contactPhone: booking.contactPhone ?? "",
    title: booking.title ?? "",
    chargeType: (booking.amount != null ? "paid" : "free") as "free" | "paid",
    amount: booking.amount,
    notes: booking.notes ?? "",
  };

  return (
    <>
      <Header>{roomBookingEditBreadcrumb}</Header>

      <Main>
        <div className="flex flex-wrap items-center justify-between space-y-2">
          <div>
            <h2 className="flex items-center font-bold text-2xl tracking-tight">
              ແກ້ໄຂການຈອງຫ້ອງ
            </h2>
            <p className="text-muted-foreground">
              ແກ້ໄຂວັນທີ / ເວລາ / ຫ້ອງ ຫຼື ຕົວເລືອກຄິດເງິນ
            </p>
          </div>

          <Button
            variant="outline"
            onClick={() =>
              nav({ to: "/app/room-bookings/$id", params: { id: id ?? "" } })
            }
          >
            <ArrowLeftIcon className="size-4" />
            ກັບຄືນ
          </Button>
        </div>

        <div className="mt-6">
          <RoomBookingForm
            mode="edit"
            initialValues={initialValues}
            excludeRoomBookingId={id ?? undefined}
            onSubmit={async (vals) => {
              try {
                await updateBooking.mutateAsync(vals as UpdateRoomBookingDTO);
                nav({ to: "/app/room-bookings" });
              } catch {
                // Error handled by mutation
              }
            }}
            submitting={updateBooking.isPending}
          />
        </div>
      </Main>
    </>
  );
}
